// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@eisinc/App/Components/Notify";
import PDFLinkCell from "@eisinc/App/Components/PaymentCenter/PDFLinkCell";
import updateSearchFields from "@eisinc/App/Handlers/Pages/InvoicePaymentHistory/UpdateSearchFields";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { InvoicePaymentHistoryDataViewContext } from "@eisinc/App/Store/Selectors/InvoicePaymentHistorySelector";
import { formatter } from "@eisinc/App/Utilities/PaymentCenterUtilities";
import { INVOICE_PAYMENT_HISTORY_SORT_KEYS } from "@eisinc/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    getInvoicePaymentHistoryApiParameter: state.xInvoicePaymentHistoryListState.getInvoicePaymentHistoryApiParameter,
    failedToLoadInvoicePaymentHistory: state.xInvoicePaymentHistoryState.failedToLoadInvoicePaymentHistory,
    language: state.context.session.language,
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = WidgetProps & HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface InvoicePaymentHistoryTableStyles {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    invoiceDateHeader?: DataTableHeaderProps;
    invoiceNumberHeader?: DataTableHeaderProps;
    poNumberHeader?: DataTableHeaderProps;
    totalHeader?: DataTableHeaderProps;
    invoiceDateCell?: DataTableCellBaseProps;
    invoiceNumberCell?: DataTableCellBaseProps;
    poNumberCell?: DataTableCellBaseProps;
    totalCell?: DataTableCellBaseProps;
}

export const tableStyles: InvoicePaymentHistoryTableStyles = {
    container: {
        css: css`
            overflow: auto;
            margin-bottom: 20px;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        color: "primary.main",
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 10px 10px 10px 0;
            margin-top: 10px;
        `,
    },
    noResultsText: {
        size: 16,
        weight: 600,
    },
    totalHeader: {
        alignX: "right",
    },
    totalCell: {
        alignX: "left",
    },
};

const styles = tableStyles;

const InvoicePaymentHistoryTable = (props: Props) => {
    const headerClick = (sortField: string) => {
        const sort = props.getInvoicePaymentHistoryApiParameter.sort === sortField ? `${sortField} Desc` : sortField;
        props.updateSearchFields({ sort });
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;

        if (props.getInvoicePaymentHistoryApiParameter.sort === sortField) {
            sorted = "ascending";
        } else if (props.getInvoicePaymentHistoryApiParameter.sort === `${sortField} Desc`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    if (props.failedToLoadInvoicePaymentHistory) {
        return null;
    }

    const invoicePaymentHistoryDataView = useContext(InvoicePaymentHistoryDataViewContext);

    if (!invoicePaymentHistoryDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (invoicePaymentHistoryDataView.value.length === 0) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Notify
                    messageType="danger"
                    notifyContainerOverrides={css`
                        max-width: 100%;
                    `}
                    body={siteMessage("Custom_No_Invoice_Payment_History_Found")}
                />
            </StyledWrapper>
        );
    }

    const rows = invoicePaymentHistoryDataView.value?.map(invoice => {
        return {
            id: invoice.id,
            invoiceNumber: invoice.accountingDoc,
            date: invoice.clearingDate
                ? getLocalizedDateTime({
                      dateTime: new Date(invoice.clearingDate),
                      language: props.language,
                  })
                : "",
            customerPO: invoice.poReference,
            total: invoice.documentCurrency,
        };
    });

    return (
        <StyledWrapper {...styles.container}>
            <DataTable {...styles.dataTable}>
                <DataTableHead {...styles.dataTableHead}>
                    <DataTableHeader
                        tight
                        sorted={sorted(INVOICE_PAYMENT_HISTORY_SORT_KEYS.PAYMENT_DATE)}
                        {...styles.invoiceDateHeader}
                        onSortClick={() => headerClick(INVOICE_PAYMENT_HISTORY_SORT_KEYS.PAYMENT_DATE)}
                    >
                        {translate("Payment Date")}
                    </DataTableHeader>
                    <DataTableHeader
                        tight
                        sorted={sorted(INVOICE_PAYMENT_HISTORY_SORT_KEYS.INVOICE_NUMBER)}
                        {...styles.invoiceNumberHeader}
                        onSortClick={() => headerClick(INVOICE_PAYMENT_HISTORY_SORT_KEYS.INVOICE_NUMBER)}
                    >
                        {translate("Invoice #")}
                    </DataTableHeader>
                    <DataTableHeader
                        tight
                        sorted={sorted(INVOICE_PAYMENT_HISTORY_SORT_KEYS.PO_REF_NUMBER)}
                        {...styles.poNumberHeader}
                        onSortClick={() => headerClick(INVOICE_PAYMENT_HISTORY_SORT_KEYS.PO_REF_NUMBER)}
                    >
                        {translate("PO / Reference #")}
                    </DataTableHeader>
                    <DataTableHeader
                        tight
                        sorted={sorted(INVOICE_PAYMENT_HISTORY_SORT_KEYS.TOTAL)}
                        {...styles.totalHeader}
                        onSortClick={() => headerClick(INVOICE_PAYMENT_HISTORY_SORT_KEYS.TOTAL)}
                    >
                        {translate("Total")}
                    </DataTableHeader>
                </DataTableHead>
                <DataTableBody {...styles.dataTableBody}>
                    {rows?.map(({ id, invoiceNumber, date, customerPO, total }, index) => (
                        <DataTableRow
                            key={id}
                            {...styles.dataTableRow}
                            data-test-selector="invoicePaymentHistory_invoiceLine"
                        >
                            <DataTableCell
                                {...styles.invoiceDateCell}
                                data-test-selector="invoicePaymentHistory_invoiceLine_date"
                            >
                                {date}
                            </DataTableCell>
                            <DataTableCell
                                {...styles.invoiceNumberCell}
                                data-test-selector="invoicePaymentHistory_invoiceLine_invoiceNumber"
                            >
                                <PDFLinkCell invoice={rows[index] as any} />
                            </DataTableCell>
                            <DataTableCell
                                {...styles.poNumberCell}
                                data-test-selector="invoicePaymentHistory_invoiceLine_customerPO"
                            >
                                {customerPO}
                            </DataTableCell>
                            <DataTableCell
                                {...styles.totalCell}
                                data-test-selector="invoicePaymentHistory_invoiceLine_total"
                            >
                                {formatter.format(total)}
                            </DataTableCell>
                        </DataTableRow>
                    ))}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(InvoicePaymentHistoryTable)),
    definition: {
        group: "Invoice Payment History" as any,
        displayName: "Search Results Table",
        allowedContexts: ["InvoicePaymentHistoryPage"],
        fieldDefinitions: [],
    },
};

export default widgetModule;
